import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Check, Star, ArrowRight, ShieldCheck, HelpCircle, ChevronDown, Calendar, Phone, MapPin, Mail, Clock, PhoneCall } from 'lucide-react';
import { COMPANY_INFO, HERO_USPS, fetchCollection, SERVICE_FAQS } from '../data';
import FormSection from '../components/FormSection';
import Gallery from '../components/Gallery';
import DynamicScrollbar from '../components/DynamicScrollbar';

// Map route paths to CMS page titles/slugs and gallery slugs
const SERVICE_MAP = {
  '/energiruder': {
    title: 'Energiruder',
    slugSuffix: 'energiruder-3',
    gallerySlug: 'energiruder-galleri',
    faqKeywords: ['energirude', 'varm kant', 'solafskærmende']
  },
  '/udskiftning-af-ruder': {
    title: 'Udskiftning af ruder',
    slugSuffix: 'udskiftning-af-ruder-3',
    gallerySlug: 'udskiftning-af-ruder-galleri',
    faqKeywords: ['udskift', 'termorude', 'varm kant']
  },
  '/termoruder': {
    title: 'Termoruder',
    slugSuffix: 'termoruder-2',
    gallerySlug: 'termoruder-galleri',
    faqKeywords: ['termorude', 'kondens', 'dug', 'varm kant']
  },
  '/glasloesninger': {
    title: 'Glasløsninger',
    slugSuffix: 'glasloesninger-2',
    gallerySlug: 'glasloesninger-galleri',
    faqKeywords: ['støjdæmpende', 'lydrude', 'solafskærmende', 'matteret']
  },
  '/gratis-besoeg': {
    title: 'Gratis besøg',
    slugSuffix: 'gratis-besoeg',
    gallerySlug: 'energiruder-galleri', // Fallback
    faqKeywords: ['besøg', 'opmåling', 'rådgivning']
  }
};

// Ydelser under menuen 'Ydelser' til intern krydslinking
const YDELSER_SERVICES = [
  {
    path: '/energiruder',
    title: 'Energiruder',
    description: 'Højisolerende energiruder der reducerer varmetab, fjerner kuldenedfald og forbedrer indeklimaet.',
    badge: 'Mest populære'
  },
  {
    path: '/udskiftning-af-ruder',
    title: 'Udskiftning af ruder',
    description: 'Faglig opmåling og udskiftning af ruder i eksisterende rammer uden unødige udgifter til nye vinduer.',
    badge: 'Bevar rammerne'
  },
  {
    path: '/termoruder',
    title: 'Termoruder',
    description: 'Udskiftning af punkterede eller duggede termoruder. Få et klart udsyn og bedre komfort tilbage.',
    badge: 'Slut med dug'
  },
  {
    path: '/glasloesninger',
    title: 'Glasløsninger',
    description: 'Specialglas, lyddæmpende ruder, solafskærmning, matteret glas og lamineret sikkerhedsglas.',
    badge: 'Specialglas'
  }
];

const SLIDESHOW_ITEMS = [
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/moderne-erhvervsbyggeri-store-glasfacader.png",
    alt: "Moderne erhvervsbyggeri med store energieffektive glasfacader og grønne tage"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/moderne-villa-store-energiruder.jpg",
    alt: "Moderne villa med store gulv-til-loft energiruder og naturligt lysindfald"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/hvidt-parcelhus-terrasse-glaspartier.jpg",
    alt: "Hvidt parcelhus med store glaspartier og udgang til fliseterrasse"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/panoramavindue-havudsigt-traeterrasse.png",
    alt: "Udsigt over træterrasse og hav gennem energieffektive panoramavinduer"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/modern-danish-home-windows.jpg",
    alt: "Moderne dansk bolig med nye energirigtige vinduer og optimal varmeisolering"
  },
  {
    url: "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/parcelhus-energiruder.jpg",
    alt: "Klassisk dansk parcelhus med nyligt udskiftede energiruder"
  }
];

export default function ServicePage() {
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [openFaqIdx, setOpenFaqIdx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const currentService = SERVICE_MAP[location.pathname] || SERVICE_MAP['/energiruder'];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDESHOW_ITEMS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    async function loadPageAndFaqs() {
      try {
        setLoading(true);
        const pages = await fetchCollection('pages');
        const matchedPage = pages.find(
          (p) => p.title === currentService.title || p.slug?.da === currentService.slugSuffix
        );
        setPageData(matchedPage);

        // Load FAQs for current service (5 relevant questions with 3-5 line answers)
        const relevantFaqs = SERVICE_FAQS[location.pathname] || SERVICE_FAQS['/energiruder'];
        setFaqs(relevantFaqs);
      } catch (error) {
        console.error('Error loading service page:', error);
      } finally {
        setLoading(false);
      }
    }
    loadPageAndFaqs();
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-brand-dark/50 pt-20">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-blue border-t-transparent mb-4" />
        <p className="text-base">Indlæser ydelsesside...</p>
      </div>
    );
  }

  return (
    <div className="pt-20 space-y-20">
      {/* Hero Section & Scrollbar */}
      <div className="space-y-0">
        <section className="relative py-12 sm:py-16 lg:py-20 overflow-hidden">
          {/* Background Slideshow */}
          <div className="absolute inset-0 z-0">
            {SLIDESHOW_ITEMS.map((item, idx) => (
              <img
                key={item.url}
                src={item.url}
                alt={item.alt}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                  idx === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
            {/* Overlay to match section's design (light gradient) */}
            <div className="absolute inset-0 bg-gradient-to-b from-brand-light/90 via-white/85 to-white/95 mix-blend-normal" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
              {/* Left Column: Value Proposition */}
              <div className="lg:col-span-7 space-y-6 text-left">
                {/* Social Proof Badge & Local Area */}
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={COMPANY_INFO.trustpilotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white border border-brand-light px-3.5 py-1.5 rounded-full shadow-sm hover:shadow-md transition-all group"
                  >
                    <span className="flex items-center gap-1 text-brand-green font-bold text-sm">
                      <Star className="w-4 h-4 fill-brand-green" />
                      Trustpilot
                    </span>
                    <span className="text-brand-dark/40 font-bold text-xs">•</span>
                    <span className="text-brand-dark/80 text-xs font-semibold group-hover:text-brand-blue transition-colors">
                      {COMPANY_INFO.trustpilotRating} ud af 5 stjerner ({COMPANY_INFO.trustpilotCount} anmeldelser)
                    </span>
                  </a>
                  <span className="inline-flex items-center gap-1.5 bg-white/90 border border-brand-light px-3 py-1.5 rounded-full text-xs font-semibold text-brand-deep shadow-xs">
                    <MapPin className="w-3.5 h-3.5 text-brand-blue" />
                    Base i Smørum – dækker Egedal, Nordsjælland og store dele af Sjælland
                  </span>
                </div>

                {/* Headline */}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-deep leading-tight tracking-tight">
                  {pageData?.meta_title ? (
                    pageData.meta_title.includes(' | ') ? (
                      (() => {
                        const [main, secondary] = pageData.meta_title.split(' | ');
                        return (
                          <>
                            <span className="block">{main}</span>
                            <span className="block text-xl sm:text-2xl lg:text-3xl font-semibold text-brand-blue mt-1 sm:mt-2">
                              {secondary}
                            </span>
                          </>
                        );
                      })()
                    ) : (
                      pageData.meta_title
                    )
                  ) : (
                    `${currentService.title} - Energirude Eksperten`
                  )}
                </h1>

                {/* Subtitle */}
                <p className="text-base sm:text-lg text-brand-dark/80 leading-relaxed max-w-2xl">
                  {(() => {
                    const text = pageData?.meta_description || 'Få professionel rådgivning og montering af ruder i høj kvalitet.';
                    if (text === 'Book et gratis besøg og få professionel rådgivning om energiruder, besparelser og den bedste løsning til din bolig.') {
                      return (
                        <>
                          Book et <span className="font-bold text-brand-deep">gratis besøg</span> og få <span className="font-bold text-brand-deep">professionel rådgivning</span> om energiruder, besparelser og den bedste løsning til din bolig.
                        </>
                      );
                    }
                    return text;
                  })()}
                </p>

                {/* 4 USPs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  {HERO_USPS.map((usp, idx) => (
                    <div key={idx} className="flex items-center gap-2.5">
                      <span className="bg-brand-green/10 text-brand-green p-1 rounded-full border border-brand-green/20 shrink-0">
                        <Check className="w-4 h-4" />
                      </span>
                      <span className="text-sm sm:text-base font-semibold text-brand-deep">
                        {usp}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Trustpilot Visual Widget */}
                <div className="pt-4 border-t border-brand-light flex flex-col sm:flex-row sm:items-center gap-4 text-xs text-brand-dark/60">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-brand-green text-brand-green" />
                    ))}
                    <span className="font-bold text-brand-deep ml-1">Fremragende</span>
                  </div>
                  <span className="hidden sm:inline text-brand-dark/20">|</span>
                  <p>
                    <Link to="/" className="font-semibold text-brand-deep hover:text-brand-blue underline decoration-brand-blue/30 underline-offset-2 transition-colors">Energirude Eksperten</Link> er specialister i energiruder. <span className="font-semibold text-brand-deep">Gratis besøg og rådgivning</span> er altid uforpligtende.
                  </p>
                </div>
              </div>

              {/* Right Column: CTA Form */}
              <div className="lg:col-span-5">
                <FormSection
                  title="Book et gratis besøg"
                  subtitle="Find ud af hvor meget I kan spare, samt hvilken løsning der passer bedst til jer."
                />
              </div>
            </div>
          </div>
        </section>
        <DynamicScrollbar />
      </div>

      {/* Main Content & Gallery */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column: Rich Text Content */}
          <div className="lg:col-span-7 space-y-8">
            {pageData?.content ? (
              <div
                className="prose prose-brand max-w-none text-brand-dark/90 leading-relaxed
                  prose-headings:text-brand-deep prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:sm:text-3xl prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-sm prose-p:sm:text-base prose-p:mb-4
                  prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-4
                  prose-li:text-sm prose-li:sm:text-base prose-li:mb-1.5
                  prose-strong:text-brand-deep prose-strong:font-bold"
                dangerouslySetInnerHTML={{ __html: pageData.content }}
              />
            ) : (
              <p className="text-brand-dark/60">Indholdet er under opbygning...</p>
            )}

            {/* Gallery Component */}
            <div className="pt-8 border-t border-brand-light">
              <Gallery slug={currentService.gallerySlug} />
            </div>
          </div>

          {/* Right Column: Sidebar & Internal Links */}
          <div className="lg:col-span-5">
            <div className="space-y-5 lg:sticky lg:top-24">
              {/* Quick Benefits Highlight Card for Energiruder */}
              {location.pathname === '/energiruder' && (
                <div className="bg-gradient-to-br from-brand-deep to-slate-900 border border-brand-blue/30 p-5 sm:p-6 space-y-3.5 shadow-md">
                  <div className="inline-flex items-center gap-2 bg-brand-blue/30 border border-brand-blue/40 px-2.5 py-1 text-xs font-semibold text-brand-light">
                    <ShieldCheck className="w-3.5 h-3.5 text-brand-blue" />
                    Glarmester med 25+ års erfaring
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">
                    Kort om energiruder
                  </h3>
                  <ul className="space-y-2 text-xs sm:text-sm text-brand-light/90 pt-0.5">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Mindre varmetab:</strong> Op til 40% bedre isolering end ældre termoruder.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Bevar rammerne:</strong> Udskiftning af glasset alene sparer både tid og penge.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Slut med træk:</strong> Varm kant reducerer kuldenedfald og kondens.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Lokal glarmester:</strong> Base i Smørum – vi dækker Egedal, Nordsjælland og store dele af Sjælland.</span>
                    </li>
                  </ul>
                  <div className="pt-1">
                    <Link
                      to="/gratis-besoeg"
                      className="inline-flex items-center justify-center w-full bg-brand-blue hover:bg-white hover:text-brand-deep text-white font-bold py-2.5 px-4 text-xs sm:text-sm transition-all shadow-sm"
                    >
                      Book gratis opmåling
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </div>
                </div>
              )}

              {/* Quick Benefits Highlight Card for Udskiftning af ruder */}
              {location.pathname === '/udskiftning-af-ruder' && (
                <div className="bg-gradient-to-br from-brand-deep to-slate-900 border border-brand-blue/30 p-5 sm:p-6 space-y-3.5 shadow-md">
                  <div className="inline-flex items-center gap-2 bg-brand-blue/30 border border-brand-blue/40 px-2.5 py-1 text-xs font-semibold text-brand-light">
                    <ShieldCheck className="w-3.5 h-3.5 text-brand-blue" />
                    Glarmester med 25+ års erfaring
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">
                    Kort om udskiftning af ruder
                  </h3>
                  <ul className="space-y-2 text-xs sm:text-sm text-brand-light/90 pt-0.5">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Bevar rammerne:</strong> Udskift kun glasset og undgå udskiftning af hele vinduet.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Punkterede ruder:</strong> Få igen et klart udsyn og slip for dug og gråt slør.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Flere funktioner:</strong> Vælg mellem energiruder, lydglas, solafskærmning og sikkerhedsglas.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Lokal glarmester:</strong> Base i Smørum – dækker Egedal, Nordsjælland og store dele af Sjælland.</span>
                    </li>
                  </ul>
                  <div className="pt-1">
                    <Link
                      to="/gratis-besoeg"
                      className="inline-flex items-center justify-center w-full bg-brand-blue hover:bg-white hover:text-brand-deep text-white font-bold py-2.5 px-4 text-xs sm:text-sm transition-all shadow-sm"
                    >
                      Book gratis opmåling
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </div>
                </div>
              )}

              {/* Quick Benefits Highlight Card for Termoruder */}
              {location.pathname === '/termoruder' && (
                <div className="bg-gradient-to-br from-brand-deep to-slate-900 border border-brand-blue/30 p-5 sm:p-6 space-y-3.5 shadow-md">
                  <div className="inline-flex items-center gap-2 bg-brand-blue/30 border border-brand-blue/40 px-2.5 py-1 text-xs font-semibold text-brand-light">
                    <ShieldCheck className="w-3.5 h-3.5 text-brand-blue" />
                    Glarmester med 25+ års erfaring
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">
                    Kort om termoruder
                  </h3>
                  <ul className="space-y-2 text-xs sm:text-sm text-brand-light/90 pt-0.5">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Punkterede ruder:</strong> Udskift ruden ved dug, skjolder eller sløret udsyn mellem glassene.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Bevar vinduet:</strong> Er karme og rammer sunde, skifter vi ofte kun selve glasset.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Moderne funktioner:</strong> Vælg mellem energiglas, lydglas, solafskærmning og varm kant.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                      <span><strong>Lokal ekspert:</strong> Base i Smørum – vi dækker Egedal, Nordsjælland og store dele af Sjælland.</span>
                    </li>
                  </ul>
                  <div className="pt-1">
                    <Link
                      to="/gratis-besoeg"
                      className="inline-flex items-center justify-center w-full bg-brand-blue hover:bg-white hover:text-brand-deep text-white font-bold py-2.5 px-4 text-xs sm:text-sm transition-all shadow-sm"
                    >
                      Book gratis opmåling
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </div>
                </div>
              )}

              {/* Enhanced Contact Info Box */}
              <div className="bg-white border-2 border-brand-blue/30 p-5 sm:p-6 space-y-4 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-blue via-brand-deep to-brand-blue" />

                <div className="space-y-1">
                  <div className="inline-flex items-center gap-1.5 text-[11px] font-bold text-brand-blue uppercase tracking-wider">
                    <PhoneCall className="w-3.5 h-3.5 text-brand-blue" />
                    Direkte glarmesterkontakt
                  </div>
                  <h3 className="text-lg font-bold text-brand-deep">
                    Har du spørgsmål?
                  </h3>
                  <p className="text-xs text-brand-dark/75 leading-relaxed">
                    Få en uforpligtende snak om energiruder og mulighederne for din bolig.
                  </p>
                </div>

                {/* Direct Call Button (Fremtrædende direkte opkald) */}
                <a
                  href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
                  className="w-full bg-brand-deep hover:bg-brand-blue text-white font-bold py-3 px-4 text-sm sm:text-base flex items-center justify-center gap-2.5 transition-all shadow-sm group border border-brand-deep hover:border-brand-blue"
                >
                  <PhoneCall className="w-4 h-4 text-brand-blue group-hover:text-white transition-colors animate-pulse" />
                  <span>Ring direkte: {COMPANY_INFO.phone}</span>
                </a>

                {/* Contact Details Grid (Tlf, E-mail, Åbningstider, Område) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1 text-xs border-t border-brand-light">
                  {/* Telefon */}
                  <div className="bg-brand-light/40 border border-brand-light p-2.5 space-y-0.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-brand-dark/70 uppercase tracking-wider">
                      <Phone className="w-3 h-3 text-brand-blue" />
                      <span>Telefon</span>
                    </div>
                    <a
                      href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
                      className="text-brand-deep hover:text-brand-blue font-bold transition-colors block text-sm"
                    >
                      {COMPANY_INFO.phone}
                    </a>
                    <span className="text-[11px] text-brand-dark/60 block">Direkte opkald</span>
                  </div>

                  {/* E-mail */}
                  <div className="bg-brand-light/40 border border-brand-light p-2.5 space-y-0.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-brand-dark/70 uppercase tracking-wider">
                      <Mail className="w-3 h-3 text-brand-blue" />
                      <span>E-mail</span>
                    </div>
                    <a
                      href={`mailto:${COMPANY_INFO.email}`}
                      className="text-brand-deep hover:text-brand-blue font-semibold transition-colors block text-xs truncate"
                      title={COMPANY_INFO.email}
                    >
                      {COMPANY_INFO.email}
                    </a>
                    <span className="text-[11px] text-brand-dark/60 block">Svar inden 24 timer</span>
                  </div>

                  {/* Åbningstider */}
                  <div className="bg-brand-light/40 border border-brand-light p-2.5 space-y-0.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-brand-dark/70 uppercase tracking-wider">
                      <Clock className="w-3 h-3 text-brand-blue" />
                      <span>Åbningstider</span>
                    </div>
                    <span className="text-brand-deep font-semibold block text-xs">
                      Man – Søn: 08 – 18
                    </span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-brand-green font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-green inline-block animate-pulse" />
                      Klar ved telefonen
                    </span>
                  </div>

                  {/* Område */}
                  <div className="bg-brand-light/40 border border-brand-light p-2.5 space-y-0.5">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-brand-dark/70 uppercase tracking-wider">
                      <MapPin className="w-3 h-3 text-brand-blue" />
                      <span>Område</span>
                    </div>
                    <span className="text-brand-deep font-semibold block text-xs">
                      Base i Smørum
                    </span>
                    <span className="text-[11px] text-brand-dark/60 block">
                      Egedal, Nordsjælland og store dele af Sjælland
                    </span>
                  </div>
                </div>

                {/* Bottom reassurance micro-badge */}
                <div className="pt-1.5 border-t border-brand-light flex items-center justify-between text-[11px] text-brand-dark/70">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-brand-green" />
                    Uforpligtende rådgivning
                  </span>
                  <span>Hurtig tilbagemelding</span>
                </div>
              </div>

            {/* Other Services Links */}
            <div className="bg-white border border-brand-light p-6 sm:p-8 rounded-2xl space-y-4 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-brand-deep">Vores andre ydelser</h3>
                <span className="text-[11px] font-bold text-brand-blue uppercase tracking-wider bg-brand-light px-2.5 py-0.5 rounded-full">
                  Glarmester
                </span>
              </div>
              <p className="text-xs text-brand-dark/70">
                Udforsk vores øvrige ydelser inden for ruder og energioptimering:
              </p>
              <div className="divide-y divide-brand-light">
                {YDELSER_SERVICES
                  .filter((service) => service.path !== location.pathname)
                  .map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className="flex items-center justify-between py-3 text-sm sm:text-base font-semibold text-brand-deep hover:text-brand-blue transition-colors group"
                    >
                      <div className="flex flex-col">
                        <span>{service.title}</span>
                        <span className="text-[11px] text-brand-dark/50 font-normal">{service.badge}</span>
                      </div>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 text-brand-blue shrink-0" />
                    </Link>
                  ))}
              </div>
            </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-brand-warm/30 py-16 border-y border-brand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
              Processen
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep mt-3">
              Sådan foregår udskiftningen
            </h2>
            <p className="text-sm sm:text-base text-brand-dark/70 mt-2">
              Vi sikrer en hurtig, professionel og fuldstændig smertefri proces fra første kontakt til færdig løsning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-brand-light shadow-sm space-y-4">
              <div className="bg-brand-light p-3 rounded-xl inline-block text-brand-blue">
                <Calendar className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-brand-deep">1. Opmåling & Rådgivning</h3>
              <p className="text-xs sm:text-sm text-brand-dark/80 leading-relaxed">
                Vi har base i Smørum og kommer gerne forbi til et gratis besøg i Egedal, Nordsjælland og store dele af Sjælland, foretager præcis opmåling og rådgiver dig om det bedste valg af ruder.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-brand-light shadow-sm space-y-4">
              <div className="bg-brand-light p-3 rounded-xl inline-block text-brand-blue">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-brand-deep">2. Produktion & Levering</h3>
              <p className="text-xs sm:text-sm text-brand-dark/80 leading-relaxed">
                Dine nye ruder produceres efter præcise mål i højeste kvalitet. Vi leverer dem sikkert til din adresse på montagedagen.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-brand-light shadow-sm space-y-4">
              <div className="bg-brand-light p-3 rounded-xl inline-block text-brand-blue">
                <Check className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-brand-deep">3. Montering & Oprydning</h3>
              <p className="text-xs sm:text-sm text-brand-dark/80 leading-relaxed">
                Vores erfarne montører udskifter ruderne hurtigt og effektivt. Vi rydder pænt op, bortkører de gamle ruder og efterlader alt rent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Relevant FAQs Section */}
      {faqs.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full">
              FAQ
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep mt-3">
              Ofte stillede spørgsmål om {currentService.title.toLowerCase()}
            </h2>
            <p className="text-sm sm:text-base text-brand-dark/70 mt-2">
              Få svar på de mest almindelige spørgsmål vedrørende denne ydelse.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaqIdx === idx;
              return (
                <div
                  key={faq.id}
                  className="bg-white border border-brand-light rounded-xl overflow-hidden transition-all"
                >
                  <button
                    onClick={() => setOpenFaqIdx(isOpen ? null : idx)}
                    className="flex items-center justify-between w-full p-5 text-left font-bold text-brand-deep hover:text-brand-blue transition-colors gap-4"
                  >
                    <span className="text-sm sm:text-base">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-brand-blue shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-0 border-t border-brand-light/50">
                      <div
                        className="text-sm sm:text-base text-brand-dark/80 leading-relaxed pt-4 prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Dedikeret krydslink sektion mellem alle ydelser */}
      <section className="bg-brand-light/35 py-16 border-t border-brand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-white border border-brand-light px-3.5 py-1 rounded-full shadow-2xs">
              Krydslinks & Ydelser
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-deep mt-3">
              Se også vores andre glarmesterydelser
            </h2>
            <p className="text-sm sm:text-base text-brand-dark/70 mt-2">
              Som erfaren glarmester tilbyder vi totalløsninger inden for ruder og energioptimering. Klik videre og læs mere om vores øvrige ydelser:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {YDELSER_SERVICES.filter((s) => s.path !== location.pathname).slice(0, 3).map((service) => (
              <Link
                key={service.path}
                to={service.path}
                className="bg-white p-6 sm:p-7 rounded-2xl border border-brand-light shadow-xs hover:shadow-md hover:border-brand-blue/50 transition-all flex flex-col justify-between group"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-brand-blue bg-brand-light px-2.5 py-1 rounded-full uppercase tracking-wider">
                      {service.badge}
                    </span>
                    <ArrowRight className="w-4 h-4 text-brand-blue group-hover:translate-x-1 transition-transform" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-brand-deep group-hover:text-brand-blue transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-brand-dark/75 leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div className="pt-4 mt-4 border-t border-brand-light/80 flex items-center justify-between text-xs font-semibold text-brand-blue group-hover:text-brand-deep transition-colors">
                  <span>Læs mere om {service.title.toLowerCase()}</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-brand-deep text-white rounded-3xl p-8 sm:p-12 lg:p-16 relative overflow-hidden border border-brand-blue/20 shadow-xl text-center space-y-6">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(36,119,199,0.15),transparent_60%)] pointer-events-none" />
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight max-w-3xl mx-auto leading-tight">
            Få et uforpligtende tilbud på {currentService.title.toLowerCase()}
          </h2>
          <p className="text-sm sm:text-base text-brand-light/80 max-w-2xl mx-auto">
            Book et gratis besøg af vores glarmester, der med base i Smørum dækker Egedal, Nordsjælland og store dele af Sjælland med præcis opmåling og rådgivning om den mest optimale løsning til din bolig. Det er 100% gratis og uforpligtende.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
            <Link
              to="/gratis-besoeg"
              className="w-full sm:w-auto bg-brand-blue hover:bg-white hover:text-brand-deep text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 shadow-md"
            >
              Book gratis besøg nu
            </Link>
            <a
              href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, '')}`}
              className="w-full sm:w-auto border border-white/20 hover:bg-white/10 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
            >
              Ring på {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
